import { TemplateBlock } from '../BaseBlock';
import { generateBlockID } from '../generateBlockID';
import { JudgementBlock } from './JudgementBlock';
import { BlockRecord, BlockValue } from 'concepts/Extraction/BlocksData';

export type DomainBlockJudgements = JudgementBlock;

// Need to use interface here to allow typescript-json-schema to correctly generate enum constraints for the 'type' field
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface DomainBlock extends TemplateBlock {
  type: 'Domain';
  description: string;
  judgements: DomainBlockJudgements[];
}

export function blockIsDomain(block: TemplateBlock): block is DomainBlock {
  return block.type === 'Domain';
}

export function newDomainBlock(
  label: string,
  extraProps?: Partial<DomainBlock>
): DomainBlock {
  return {
    type: 'Domain',
    id: generateBlockID(),
    label,
    description: '',
    judgements: [],
    ...extraProps,
  };
}

export function updateLabel(domain: DomainBlock, label: string) {
  return { ...domain, label };
}

export function updateDescription(domain: DomainBlock, description: string) {
  return { ...domain, description };
}

export function addJudgement(
  domain: DomainBlock,
  judgement: DomainBlockJudgements,
  afterID: string | null
) {
  const judgements = [...domain.judgements];

  if (afterID === null) {
    judgements.unshift(judgement);
  } else {
    const index = judgements.findIndex((judgement) => judgement.id === afterID);
    if (index === -1) {
      throw new Error(`Cannot add judgement, invalid judgement id: ${afterID}`);
    }
    judgements.splice(index + 1, 0, judgement);
  }

  return { ...domain, judgements };
}

export function removeJudgement(domain: DomainBlock, id: string): DomainBlock {
  const judgements = [...domain.judgements];
  const index = judgements.findIndex((judgement) => judgement.id === id);

  if (index === -1) {
    throw new Error(`Cannot remove judgement, invalid judgement id: ${id}`);
  }

  judgements.splice(index, 1);
  return { ...domain, judgements };
}

export function moveJudgement(
  domain: DomainBlock,
  id: string,
  toIndex: number
): DomainBlock {
  const judgements = [...domain.judgements];

  const movedIndex = judgements.findIndex((judgement) => judgement.id === id);
  if (movedIndex === -1) {
    throw new Error(`Cannot move judgement, invalid judgement id: ${id}`);
  }

  const [moved] = judgements.splice(movedIndex, 1);

  if (toIndex < 0 || toIndex >= judgements.length) {
    throw new Error(`Cannot move judgement, invalid index: ${toIndex}`);
  }

  judgements.splice(toIndex, 0, moved);
  return { ...domain, judgements };
}

export function updateJudgement(
  domain: DomainBlock,
  newJudgement: DomainBlockJudgements
): DomainBlock {
  const judgements = [...domain.judgements];
  const index = judgements.findIndex(
    (judgement) => judgement.id === newJudgement.id
  );
  if (index === -1) {
    throw new Error(
      `Cannot update judgement, invalid judgement id: ${newJudgement.id}`
    );
  }
  judgements.splice(index, 1, newJudgement);
  return { ...domain, judgements };
}

export function readDomainSelectedValue(
  record: BlockRecord<BlockValue>
): string | null {
  return typeof record?.value === 'string' ? record.value : null;
}
